/**
 * Auth UI State Slice
 * Handles LOCAL UI state only (not API calls)
 * API calls are handled by features/auth/authApiSlice.ts
 */

import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { getStoredUser } from "../../utils/session";

export interface UserDetails {
  displayName: string | null;
  username: string | null;
  role: string | null;
  isAuthenticated: boolean;
}

export interface AuthState {
  userDetails: UserDetails;
}

// Initialize state from localStorage if available (for display purposes only)
// Authentication is handled by HttpOnly cookies
const storedUser = getStoredUser();

const initialState: AuthState = {
  userDetails: {
    displayName: storedUser?.displayName || null,
    username: storedUser?.username || null,
    role: storedUser?.role || null,
    isAuthenticated: !!storedUser, // Will be validated by getCurrentUser API call
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Set user credentials in UI state
     * Called after successful login/token verification
     */
    setCredentials: (
      state,
      action: PayloadAction<{
        displayName: string;
        username: string;
        role: string;
        isAuthenticated: boolean;
      }>
    ) => {
      state.userDetails = {
        displayName: action.payload.displayName,
        username: action.payload.username,
        role: action.payload.role,
        isAuthenticated: action.payload.isAuthenticated,
      };
    },

    /**
     * Clear user credentials from UI state
     * Called after logout
     */
    logout: (state) => {
      state.userDetails = {
        displayName: null,
        username: null,
        role: null,
        isAuthenticated: false,
      };
    },
  },
});

export const { setCredentials, logout } = authSlice.actions;

/**
 * Memoized selectors for auth state
 * Prevents unnecessary re-renders
 */

// Base selector
const selectAuthState = (state: { auth: AuthState }) => state.auth;

// User details selector
export const selectCurrentUser = createSelector(
  [selectAuthState],
  (auth) => auth.userDetails
);

// Authentication status selector
export const selectIsAuthenticated = createSelector(
  [selectCurrentUser],
  (user) => user.isAuthenticated
);

// Display name selector
export const selectDisplayName = createSelector(
  [selectCurrentUser],
  (user) => user.displayName
);

// Username selector
export const selectUsername = createSelector(
  [selectCurrentUser],
  (user) => user.username
);

// User role selector
export const selectUserRole = createSelector(
  [selectCurrentUser],
  (user) => user.role
);

// Role-based permission selectors
export const selectIsAdmin = createSelector(
  [selectUserRole],
  (role) => role === "admin"
);

export const selectIsManager = createSelector(
  [selectUserRole],
  (role) => role === "manager" || role === "admin"
);

export const selectCanExport = createSelector(
  [selectUserRole],
  (role) => role === "admin" || role === "manager"
);

/**
 * Composite selector for complete auth state
 */
export const selectAuthInfo = createSelector([selectCurrentUser], (user) => ({
  isAuthenticated: user.isAuthenticated,
  displayName: user.displayName,
  username: user.username,
  role: user.role,
}));

export default authSlice.reducer;
